import { Platform } from 'react-native';
import { endConnection, fetchProducts, finishTransaction, getActiveSubscriptions, getAvailablePurchases, getPromotedProductIOS, getReceiptDataIOS, initConnection, isEligibleForIntroOfferIOS, promotedProductListenerIOS, requestPurchase, requestReceiptRefreshIOS, } from 'utils/packages/iap';
import Sentry from 'utils/sentry';
import { utils as firebaseUtils } from 'utils/packages/rnFirebase';
import Modal from 'utils/packages/Modal';
import { itemSkus, subscriptionGroupId, SubscriptionPlatform, Tier, tierGreaterThan, TierPeriod, } from './entities';
export const getItemSKUForProductId = (productId) => itemSkus.find((sku) => sku.id === productId);
/**
 * The billing period of the subscription the user is on now.
 *
 * `emmaProStatus.period` is lowercase where `TierPeriod` is upper, so the SKU is
 * the authoritative source and the status is only a fallback.
 */
const getProStatusPeriod = (emmaProStatus) => {
    const skuPeriod = emmaProStatus.productId
        ? getItemSKUForProductId(emmaProStatus.productId)?.period
        : undefined;
    if (skuPeriod) {
        return skuPeriod;
    }
    if (emmaProStatus.period === 'yearly')
        return TierPeriod.yearly;
    if (emmaProStatus.period === 'monthly')
        return TierPeriod.monthly;
    return undefined;
};
/**
 * Google Play Billing subscription replacement (proration) mode.
 *
 * @see https://developer.android.com/google/play/billing/subscriptions#replacement-modes
 */
export const getAndroidProrationMode = (subscription, emmaProStatus) => {
    if (!emmaProStatus?.tier || emmaProStatus.tier === Tier.free) {
        return undefined;
    }
    const newTier = subscription.tier;
    const oldTier = emmaProStatus.tier;
    /**
     * Transitioning to a higher tier, like pro -> ultimate:
     *
     * The subscription is upgraded immediately.
     * Any time remaining is adjusted based on the price difference, and credited toward the new subscription by pushing forward the next billing date. This is the default behavior.
     */
    if (tierGreaterThan(newTier, oldTier)) {
        return 'with-time-proration';
    }
    // handle upgrade tier BUT billing period shorter USE WITH_TIME_PROBATION
    if (subscription.productId === emmaProStatus.productId) {
        // We’re using a cancellation offer to try retain the user, we cannot use defferred
        return 'without-proration';
    }
    /**
     * Same tier, monthly -> yearly.
     *
     * Proration switches the plan immediately with no charge today, crediting the
     * unused time by pushing the next billing date out, and mints an ordinary
     * single-product purchase.
     */
    if (newTier === oldTier &&
        subscription.period === TierPeriod.yearly &&
        getProStatusPeriod(emmaProStatus) === TierPeriod.monthly) {
        return 'with-time-proration';
    }
    /**
     * Other cases, like:
     * - Transitioning to a lower tier, like ultimate -> pro
     * - Same tier, transitioning to a shorter period, like yearly -> monthly
     *
     * The subscription is upgraded or downgraded only when the subscription renews.
     */
    return 'deferred';
};
let iapInitialized = false;
export const safeInitIAP = async () => {
    try {
        if (!iapInitialized) {
            iapInitialized = await initConnection();
        }
    }
    catch (err) {
        Sentry.logError({
            filename: 'features/premium/iap.ts',
            message: 'Failed to initialize expo-iap',
            err,
        });
    }
    return iapInitialized;
};
export const finalizeIAP = async () => {
    try {
        if (iapInitialized) {
            await endConnection();
            iapInitialized = false;
        }
    }
    catch {
        // No op
    }
};
export const getIsEligibleForIntroOffer = async () => {
    try {
        if (Platform.OS !== 'ios') {
            return undefined;
        }
        if (!(await safeInitIAP())) {
            return undefined;
        }
        return await isEligibleForIntroOfferIOS(subscriptionGroupId);
    }
    catch {
        return undefined;
    }
};
const mapIosPaymentMode = (mode) => {
    switch (mode) {
        case 'free-trial':
            return 'FREETRIAL';
        case 'pay-as-you-go':
            return 'PAYASYOUGO';
        case 'pay-up-front':
            return 'PAYUPFRONT';
        default:
            return '';
    }
};
const mapIosPeriod = (period) => {
    if (!period)
        return '';
    const value = String(period).toUpperCase();
    if (value.includes('YEAR') || /P\d+Y/.test(value))
        return 'YEAR';
    if (value.includes('MONTH') || /P\d+M/.test(value))
        return 'MONTH';
    if (value.includes('WEEK') || /P\d+W/.test(value))
        return 'WEEK';
    if (value.includes('DAY') || /P\d+D/.test(value))
        return 'DAY';
    return value;
};
const normalizeAndroidSubscription = (product) => ({
    platform: SubscriptionPlatform.android,
    productId: product.id,
    productType: 'subs',
    description: product.description,
    name: product.nameAndroid,
    title: product.title,
    subscriptionOfferDetails: (product.subscriptionOffers ?? []).map((offer) => ({
        basePlanId: offer.basePlanIdAndroid ?? '',
        offerId: offer.id || null,
        offerTags: offer.offerTagsAndroid ?? [],
        offerToken: offer.offerTokenAndroid ?? '',
        pricingPhases: {
            pricingPhaseList: (offer.pricingPhasesAndroid?.pricingPhaseList ?? []).map((phase) => ({
                billingCycleCount: phase.billingCycleCount,
                billingPeriod: phase.billingPeriod,
                formattedPrice: phase.formattedPrice,
                priceAmountMicros: phase.priceAmountMicros,
                priceCurrencyCode: phase.priceCurrencyCode,
                recurrenceMode: phase.recurrenceMode,
            })),
        },
    })),
});
const normalizeIosSubscription = (product) => ({
    platform: SubscriptionPlatform.ios,
    productId: product.id,
    type: 'subs',
    description: product.description,
    title: product.title,
    price: product.price != null ? String(product.price) : '',
    currency: product.currency,
    localizedPrice: product.displayPrice,
    discounts: (product.subscriptionOffers ?? []).map((offer) => ({
        identifier: offer.id,
        type: offer.type,
        localizedPrice: offer.localizedPriceIOS ?? offer.displayPrice,
        numberOfPeriods: String(offer.numberOfPeriodsIOS ?? offer.periodCount),
        paymentMode: mapIosPaymentMode(offer.paymentMode),
        price: String(offer.price),
        subscriptionPeriod: mapIosPeriod(offer.period?.unit),
    })),
    introductoryPrice: product.introductoryPriceIOS ?? undefined,
    introductoryPriceAsAmountIOS: product.introductoryPriceAsAmountIOS ?? undefined,
    introductoryPriceNumberOfPeriodsIOS: product.introductoryPriceNumberOfPeriodsIOS ?? undefined,
    introductoryPricePaymentModeIOS: mapIosPaymentMode(product.introductoryPricePaymentModeIOS),
    introductoryPriceSubscriptionPeriodIOS: mapIosPeriod(product.introductoryPriceSubscriptionPeriodIOS),
});
const isSubscription = (product) => product.type === 'subs';
const normalizeSubscription = (product) => {
    if (!isSubscription(product))
        return undefined;
    if (product.platform === 'android') {
        return normalizeAndroidSubscription(product);
    }
    if (product.platform === 'ios') {
        return normalizeIosSubscription(product);
    }
    return undefined;
};
export const getIapSubscriptions = async () => {
    const libSubscriptions = await getIapLibSubscriptions(itemSkus.map((e) => e.id));
    return itemSkus
        .map((it) => {
        const sku = libSubscriptions.find((o) => o.productId === it.id);
        return sku
            ? {
                ...it,
                ...sku,
            }
            : undefined;
    })
        .reduce((prev, curr) => (curr ? [...prev, curr] : prev), []);
};
export const getBaseSubscriptionOfferAndroid = (iapSubscription) => {
    const allOffers = iapSubscription.platform === SubscriptionPlatform.android
        ? iapSubscription.subscriptionOfferDetails ?? []
        : [];
    const baseTrialOffer = allOffers.find((offer) => offer.offerTags &&
        offer.offerTags.length === 2 &&
        offer.offerTags.includes('freetrial') &&
        offer.offerTags.includes('base'));
    if (baseTrialOffer) {
        return baseTrialOffer;
    }
    const basePriceOffer = allOffers.find((offer) => offer.offerTags &&
        // Child offers inherit the base offer too, so the length MUST be 1
        offer.offerTags.length === 1 &&
        offer.offerTags.includes('base'));
    if (basePriceOffer) {
        return basePriceOffer;
    }
    return allOffers[0];
};
export const getBaseSubscriptionPriceAndroid = (baseOffer) => {
    if (!baseOffer) {
        return undefined;
    }
    const firstNonZeroPricingPhase = (baseOffer.pricingPhases?.pricingPhaseList ?? []).find((phase) => Number(phase.priceAmountMicros) > 0);
    if (firstNonZeroPricingPhase?.priceAmountMicros != null) {
        return {
            // Converting micro-units to currency units
            price: (Number(firstNonZeroPricingPhase.priceAmountMicros) / 1000000).toFixed(2),
            currency: firstNonZeroPricingPhase.priceCurrencyCode,
            localizedPrice: firstNonZeroPricingPhase.formattedPrice,
        };
    }
    return undefined;
};
export const getIapLibSubscriptions = async (ids) => {
    if (await safeInitIAP()) {
        let products = [];
        try {
            products = (await fetchProducts({ skus: ids, type: 'subs' })) ?? [];
        }
        catch (err) {
            Sentry.logError({
                filename: 'features/premium/iap.ts',
                message: 'Failed to fetch IAP subscriptions',
                err,
            });
            return [];
        }
        return products
            .map((product) => {
            try {
                return normalizeSubscription(product);
            }
            catch (err) {
                Sentry.logError({
                    filename: 'features/premium/iap.ts',
                    message: `Failed to normalize IAP subscription ${product.id}`,
                    err,
                });
                return undefined;
            }
        })
            .filter((sub) => sub !== undefined);
    }
    return [];
};
export const getAvailableIapPurchases = async () => {
    try {
        if (await safeInitIAP()) {
            return (await getAvailablePurchases()).sort((a, b) => b.transactionDate - a.transactionDate);
        }
    }
    catch {
        // No op
    }
    return [];
};
export const getLastPurchaseFromPurchaseHistory = async () => {
    try {
        const purchases = await getAvailableIapPurchases();
        const pendingAndroid = purchases
            .map((purchase) => purchase.pendingPurchaseUpdateAndroid)
            .find((pending) => pending?.products?.length);
        if (pendingAndroid?.products?.length) {
            return getItemSKUForProductId(pendingAndroid.products[0]);
        }
        if (Platform.OS === 'ios') {
            const active = await getActiveSubscriptions(itemSkus.map((e) => e.id));
            const pendingIos = active
                .map((sub) => sub.renewalInfoIOS?.pendingUpgradeProductId)
                .find((productId) => !!productId);
            if (pendingIos) {
                return getItemSKUForProductId(pendingIos);
            }
        }
        return undefined;
    }
    catch {
        return undefined;
    }
};
export const getLastPurchase = async () => {
    const purchases = await getAvailableIapPurchases();
    if (purchases.length > 0) {
        const subscriptions = await getIapLibSubscriptions([
            purchases[0].productId,
        ]);
        if (subscriptions.length > 0) {
            return { purchase: purchases[0], subscription: subscriptions[0] };
        }
    }
    return {};
};
export const finishSubscriptionPurchase = async (purchase) => {
    try {
        if (await safeInitIAP()) {
            await finishTransaction({ purchase, isConsumable: false });
            return true;
        }
    }
    catch (err) {
        Sentry.logError({
            filename: 'features/premium/iap.ts',
            message: `Failed to finish transaction for ${purchase.productId}`,
            err,
        });
    }
    return false;
};
/**
 * The iOS validation endpoint expects the classic base64 App Store receipt,
 * which StoreKit 2 no longer surfaces on the purchase. The receipt file may not
 * be on disk yet right after a first purchase, in which case `getReceiptDataIOS()`
 * resolves to an empty string and the receipt has to be refreshed explicitly.
 */
export const getAppleTransactionReceipt = async () => {
    const receipt = await getReceiptDataIOS();
    if (receipt) {
        return receipt;
    }
    const requestedReceipt = (await requestReceiptRefreshIOS()) || undefined;
    if (!requestedReceipt) {
        throw new Error('Failed to read the App Store receipt');
    }
    return requestedReceipt;
};
const releaseAndroidPurchase = async () => {
    if (Platform.OS !== 'android')
        return;
    await finalizeIAP();
};
export const requestSubscription = async (subscription, emmaProStatus, discount) => {
    await releaseAndroidPurchase();
    if (await safeInitIAP()) {
        await fetchProducts({ skus: [subscription.id], type: 'subs' });
        if (discount && discount.type === SubscriptionPlatform.ios) {
            return requestPurchase({
                request: {
                    apple: {
                        sku: subscription.id,
                        withOffer: discount.discount,
                    },
                },
                type: 'subs',
            });
        }
        if (Platform.OS === 'android') {
            const googleProStatus = emmaProStatus?.store === 'google' ? emmaProStatus : undefined;
            const oldProductId = googleProStatus?.productId;
            const oldPurchaseToken = oldProductId
                ? await getPurchaseTokenId(oldProductId)
                : undefined;
            const offerToken = (() => {
                if (discount) {
                    return discount.offer.offerToken;
                }
                return getBaseSubscriptionOfferAndroid(subscription)?.offerToken;
            })();
            const replacementMode = getAndroidProrationMode(subscription, googleProStatus);
            return requestPurchase({
                request: {
                    google: {
                        skus: [subscription.id],
                        purchaseToken: oldPurchaseToken,
                        subscriptionProductReplacementParams: oldPurchaseToken && oldProductId && replacementMode
                            ? { oldProductId, replacementMode }
                            : undefined,
                        subscriptionOffers: offerToken
                            ? [{ sku: subscription.id, offerToken }]
                            : [],
                    },
                },
                type: 'subs',
            });
        }
        return requestPurchase({
            request: { apple: { sku: subscription.id } },
            type: 'subs',
        });
    }
    return undefined;
};
export const getActiveIapPurchase = async (productId) => {
    const purchases = await getAvailableIapPurchases();
    if (productId) {
        const match = purchases.find((purchase) => purchase.productId === productId);
        if (match)
            return match;
    }
    return purchases[0];
};
export const checkPersistedPromotedProduct = async () => {
    try {
        const product = await getPromotedProductIOS();
        const productId = product?.id;
        if (!productId ||
            !getItemSKUForProductId(productId) ||
            !(await safeInitIAP())) {
            return;
        }
        await fetchProducts({ skus: [productId], type: 'subs' });
        await requestPurchase({
            request: { apple: { sku: productId } },
            type: 'subs',
        });
    }
    catch {
        // No op
    }
};
const getPurchaseTokenId = async (productId) => (await getAvailableIapPurchases()).find((it) => it.productId === productId)
    ?.purchaseToken ?? undefined;
export const IAPEmitter = {
    addListener: (_eventName, listener) => {
        const subscription = promotedProductListenerIOS((product) => {
            const productId = typeof product === 'string' ? product : product?.id ?? '';
            listener(productId);
        });
        return { remove: () => subscription.remove() };
    },
};
export const getIapSubscriptionPrices = (iapSubscription) => {
    if (!iapSubscription)
        return undefined;
    if (iapSubscription.platform === SubscriptionPlatform.ios) {
        return iapSubscription;
    }
    const baseSubscriptionPrice = getBaseSubscriptionPriceAndroid(getBaseSubscriptionOfferAndroid(iapSubscription));
    if (!baseSubscriptionPrice) {
        return undefined;
    }
    return {
        ...iapSubscription,
        ...baseSubscriptionPrice,
    };
};
export async function checkGooglePlayServices() {
    const utils = firebaseUtils();
    const { isAvailable, hasResolution, isUserResolvableError } = utils.playServicesAvailability;
    // all good and valid \o/
    if (isAvailable)
        return Promise.resolve();
    // if the user can resolve the issue i.e by updating play services
    // then call Google Play’s own/default prompting functionality
    if (isUserResolvableError) {
        return utils.promptForPlayServices();
    }
    // call Google Play’s own/default resolution functionality
    if (hasResolution) {
        return utils.resolutionForPlayServices();
    }
    // There’s no way to resolve play services on this device
    // probably best to show a dialog
    Modal.showError('Unable to resolve Google Play Services');
    return Promise.reject();
}
